import { useEffect, useState } from 'react';
import { CATALOG_ENABLED } from '@/lib/config';
import { fetchCatalogLibrary } from '@/features/catalog/api';

/**
 * Resolves whether the Wissenswelten catalog feature is live for this client.
 * Exact mirror of useSeriesEnabled (see lib/config.ts CATALOG_ENABLED):
 *   1. Build-time master switch CATALOG_ENABLED. When false → returns false
 *      immediately, NO network probe (feature fully dark, library byte-for-byte
 *      identical to today).
 *   2. When the build flag is true, defer to backend capability: probe
 *      GET /api/catalog/library once per session. 200 → enabled; 404
 *      (CatalogEnabledGuard dormant) or any error → disabled (fail-closed).
 *
 * Makes the production flip backend-only: ship the flag-true PWA once (still
 * dark, backend 404), then flip Railway CATALOG_ENABLED.
 */

let cached: boolean | null = null;
let inflight: Promise<boolean> | null = null;

async function probeCatalogCapability(): Promise<boolean> {
  if (cached !== null) return cached;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      await fetchCatalogLibrary();
      cached = true;
    } catch {
      // 404 (dormant guard) or any transient error → fail closed.
      cached = false;
    } finally {
      inflight = null;
    }
    return cached;
  })();
  return inflight;
}

/** Test-only: reset the module-level capability cache. */
export function __resetCatalogEnabledCache(): void {
  cached = null;
  inflight = null;
}

export function useCatalogEnabled(): { enabled: boolean; loading: boolean } {
  const [enabled, setEnabled] = useState<boolean>(CATALOG_ENABLED ? (cached ?? false) : false);
  const [loading, setLoading] = useState<boolean>(CATALOG_ENABLED && cached === null);

  useEffect(() => {
    if (!CATALOG_ENABLED || cached !== null) return;
    let active = true;
    void probeCatalogCapability().then((res) => {
      if (!active) return;
      setEnabled(res);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, []);

  return { enabled, loading };
}
